import { supabase } from '../../../shared/services/supabase';

export interface Location {
  id: string;
  name: string;
  short_code: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateLocationInput {
  name: string;
  short_code: string;
  is_active?: boolean;
}

export interface UpdateLocationInput {
  id: string;
  name?: string;
  short_code?: string;
  is_active?: boolean;
}

export const locationsService = {
  /**
   * Fetch all locations
   */
  async getLocations(): Promise<Location[]> {
    const { data, error } = await supabase
      .from('locations')
      .select('*')
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching locations:', error);
      throw new Error(error.message);
    }

    return data as Location[];
  },

  /**
   * Fetch a single location by ID
   */
  async getLocation(id: string): Promise<Location> {
    const { data, error } = await supabase
      .from('locations')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching location:', error);
      throw new Error(error.message);
    }

    return data as Location;
  },

  /**
   * Create a new location
   */
  async createLocation(input: CreateLocationInput): Promise<Location> {
    const { data, error } = await supabase
      .from('locations')
      // @ts-expect-error - Database types not regenerated yet
      .insert({
        name: input.name,
        short_code: input.short_code.toUpperCase(),
        is_active: input.is_active ?? true,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating location:', error);
      throw new Error(error.message);
    }

    // Auto-create default dashboard settings for new location
    await supabase
      .from('location_dashboard_settings')
      // @ts-expect-error - Database types not regenerated yet
      .insert({ location_id: data.id })
      .select()
      .single();

    return data as Location;
  },

  /**
   * Update an existing location
   */
  async updateLocation(input: UpdateLocationInput): Promise<Location> {
    const updateData: Partial<Location> = {};

    if (input.name !== undefined) updateData.name = input.name;
    if (input.short_code !== undefined)
      updateData.short_code = input.short_code.toUpperCase();
    if (input.is_active !== undefined) updateData.is_active = input.is_active;

    const { data, error } = await supabase
      .from('locations')
      // @ts-expect-error - Supabase type mismatch
      .update(updateData)
      .eq('id', input.id)
      .select()
      .single();

    if (error) {
      console.error('Error updating location:', error);
      throw new Error(error.message);
    }

    return data as Location;
  },

  /**
   * Delete a location (soft delete by setting is_active to false)
   */
  async deleteLocation(id: string): Promise<void> {
    const { error } = await supabase
      .from('locations')
      // @ts-expect-error - Supabase type mismatch
      .update({ is_active: false })
      .eq('id', id);

    if (error) {
      console.error('Error deleting location:', error);
      throw new Error(error.message);
    }
  },

  /**
   * Activate a location
   */
  async activateLocation(id: string): Promise<Location> {
    return this.updateLocation({ id, is_active: true });
  },

  /**
   * Deactivate a location
   */
  async deactivateLocation(id: string): Promise<Location> {
    return this.updateLocation({ id, is_active: false });
  },
};
